/** The viewer's arithmetic: coordinates in, transform out.
 *
 *  None of this touches React or the DOM, and that is not tidiness. jsdom has
 *  `TouchEvent` but no `Touch`, so a two-finger gesture cannot be synthesized in
 *  a test at all -- anything left inside the component is untestable by
 *  construction. `Viewer.tsx` therefore reads touches and applies transforms and
 *  does no maths of its own, the same split that made rows.ts worth having.
 */
export interface Pt { x: number; y: number }
export interface Box { width: number; height: number }

/** 1x is fitted. 4x is about as far as a 1440-wide original stays sharp on a
 *  phone; past that you are zooming into the upscale. */
export const MIN_SCALE = 1;
export const MAX_SCALE = 4;
/** Where a double tap lands: enough to read a face or a caption. */
export const DOUBLE_TAP_SCALE = 2.5;
/** How far you have to drag before letting go closes the viewer. */
export const DISMISS_PX = 120;

export const spread = (a: Pt, b: Pt): number => Math.hypot(b.x - a.x, b.y - a.y);

export const midpoint = (a: Pt, b: Pt): Pt =>
  ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

/** NaN is the only value that cannot be clamped into range, and it arrives from
 *  a zero-length pinch. Infinity clamps to MAX like any other large number. */
export function clampScale(s: number): number {
  if (Number.isNaN(s)) return MIN_SCALE;
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));
}

/** The scale a pinch has reached, relative to where it started.
 *
 *  The guard is not defensive padding: two fingers can land on the same pixel,
 *  and dividing by that zero gives an Infinity transform -- the photo does not
 *  stretch, it disappears.
 */
export function pinchScale(startScale: number, startSpread: number, nowSpread: number): number {
  if (!(startSpread > 0)) return clampScale(startScale);
  return clampScale(startScale * (nowSpread / startSpread));
}

/** Keep the image's edges inside the viewport.
 *
 *  At 1x there is nothing off screen, so there is no pan -- a drag that moved a
 *  fitted photo reads as a bug, not a gesture. Beyond that you may drag exactly
 *  as far as the overflow, and no further, per axis: a wide photo on a tall
 *  phone pans sideways and is pinned vertically.
 */
export function clampPan(offset: Pt, scale: number, box: Box, viewport: Box): Pt {
  const slack = (size: number, within: number) => {
    const over = size * scale - within;
    return Number.isFinite(over) && over > 0 ? over / 2 : 0;
  };
  const pin = (v: number, max: number) => {
    if (!Number.isFinite(v)) return 0;
    // `-max` of a zero slack is -0, and that reaches the DOM as
    // `translate(-0px)`. Normalised here so the transform string stays clean.
    const clamped = Math.max(-max, Math.min(max, v));
    return clamped === 0 ? 0 : clamped;
  };

  return {
    x: pin(offset.x, slack(box.width, viewport.width)),
    y: pin(offset.y, slack(box.height, viewport.height)),
  };
}

/** 0 at rest, 1 at the point of no return. Absolute, because a photo near the
 *  top of the screen is as natural to flick up as down. */
export function dismissProgress(dy: number): number {
  if (!Number.isFinite(dy)) return 0;
  return Math.min(1, Math.abs(dy) / DISMISS_PX);
}

/** A double tap on anything zoomed goes back to fitted -- after a pinch to 1.3x
 *  you want out, not further in. */
export const nextScale = (current: number): number =>
  current > MIN_SCALE ? MIN_SCALE : DOUBLE_TAP_SCALE;

/** The size the image is DRAWN at, fitted inside the screen.
 *
 *  Computed from the file's own stored dimensions rather than measured, for two
 *  reasons that point the same way: clampPan needs a box on the very first
 *  frame, before any image has loaded, and jsdom reports every rect as zero, so
 *  a measured box would make the whole viewer untestable.
 *
 *  The 3:4 fallback is rows.ts's, for rows where ffprobe read nothing. A zero
 *  box is a photo that cannot be panned and a transform full of NaN.
 */
export function fitBox(w: number | null, h: number | null, viewport: Box): Box {
  const width = w && w > 0 ? w : 3;
  const height = h && h > 0 ? h : 4;
  const scale = Math.min(viewport.width / width, viewport.height / height);
  return { width: width * scale, height: height * scale };
}
